import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * <Seo /> — thin wrapper around react-helmet-async.
 * Every public page should mount this so search engines and AI answer
 * engines have a complete <head> without relying on SSR.
 *
 * Pass optional `jsonLd` (or an array of objects) to inject one or more
 * schema.org structured-data blocks.
 */
const SITE = 'Kanjusee';
const DEFAULT_DESC =
  'Post any product or service demand at your budget. Verified Indian vendors compete with their best offers on Kanjusee — the buyer-powered reverse-bidding marketplace.';
const DEFAULT_IMAGE = '/logo512.png';

function origin() {
  if (typeof window !== 'undefined' && window.location) return window.location.origin;
  return 'https://kanjusee.com';
}

function absolute(path) {
  if (!path) return origin();
  if (/^https?:\/\//i.test(path)) return path;
  return origin().replace(/\/$/, '') + (path.startsWith('/') ? path : '/' + path);
}

export default function Seo({
  title,
  description,
  canonical,
  image,
  type = 'website',
  keywords,
  noindex = false,
  jsonLd,
}) {
  // Avoid "… | Kanjusee | Kanjusee" when backend-generated seoTitle already
  // contains the brand suffix.
  const hasBrand = !!title && new RegExp(`\\|\\s*${SITE}\\s*$`, 'i').test(title);
  const fullTitle = title
    ? (hasBrand ? title : `${title} | ${SITE}`)
    : `${SITE} — Name your price. Vendors compete.`;
  const desc = description || DEFAULT_DESC;
  const img = absolute(image || DEFAULT_IMAGE);
  const canon = absolute(canonical || (typeof window !== 'undefined' ? window.location.pathname : '/'));
  const ldBlocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      <link rel="canonical" href={canon} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canon} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {ldBlocks.map((block, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(block)}</script>
      ))}
    </Helmet>
  );
}

/** Standard JSON-LD builders — keep adjacent for consistency. */
export const jsonLd = {
  organization() {
    return {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE,
      url: origin(),
      logo: absolute('/logo512.png'),
      sameAs: [],
    };
  },
  website() {
    return {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE,
      url: origin(),
      potentialAction: {
        '@type': 'SearchAction',
        target: `${origin()}/catalog?search={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    };
  },
  breadcrumbs(trail) {
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: trail.map((t, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: t.name,
        item: absolute(t.url),
      })),
    };
  },
  itemList(items, baseListName) {
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: baseListName,
      numberOfItems: items.length,
      itemListElement: items.map((it, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: absolute(`/item/${it.slug || it._id}`),
        name: it.name,
      })),
    };
  },
  // Products w/o a fixed price: we model them as a Service/Thing with
  // potentialAction=RequestForQuote so search engines don't penalise
  // missing price fields.
  requestable(item) {
    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: item.name,
      description: item.seoDescription || item.description || '',
      image: item.image ? [absolute(item.image)] : undefined,
      brand: item.brandId?.name ? { '@type': 'Brand', name: item.brandId.name } : undefined,
      category: item.categoryId?.name,
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceSpecification: {
          '@type': 'PriceSpecification',
          priceCurrency: 'INR',
          // Reverse-bidding: the buyer proposes the price.
          description: 'Buyer-named price — vendors compete to match.',
        },
      },
    };
  },
  faq(pairs) {
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: pairs.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    };
  },
};
